/**
 * Tenant context. Fetches /public/config on app boot and exposes
 * tenant branding (name, logo, colors) to all components.
 *
 * CSS custom properties are injected into :root so Tailwind classes
 * like bg-brand-500 automatically pick up the tenant's primary color.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export interface TenantBranding {
  slug: string;
  name: string;
  tagline: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string;
  accentColor: string;
  bgColor: string;
  textColor: string;
  /** Session 4 K2: 'engine' → new videos are orders on the video engine (the goal field and a saved strategy apply). */
  videoPipeline?: "legacy" | "engine";
}

interface TenantContextValue {
  tenant: TenantBranding | null;
  siteName: string;
  loading: boolean;
}

const TenantContext = createContext<TenantContextValue>({
  tenant: null,
  siteName: "tubeforge",
  loading: true,
});

export function useTenant(): TenantContextValue {
  return useContext(TenantContext);
}

/**
 * Convert a hex color to HSL values for CSS variable injection.
 * Tailwind's color system uses HSL under the hood.
 */
function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const c = hex.replace("#", "");
  let r = parseInt(c.substring(0, 2), 16) / 255;
  let g = parseInt(c.substring(2, 4), 16) / 255;
  let b = parseInt(c.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function applyBrandingToCSS(tenant: TenantBranding): void {
  // SSR guard — skip DOM manipulation on the server
  if (typeof document === "undefined") return;

  const root = document.documentElement;

  // Inject primary color as CSS variable for Tailwind brand-* utilities
  const primary = hexToHsl(tenant.primaryColor);
  root.style.setProperty("--brand-hue", String(primary.h));
  root.style.setProperty("--brand-sat", `${primary.s}%`);

  // Direct hex values for components that use them
  root.style.setProperty("--tenant-primary", tenant.primaryColor);
  root.style.setProperty("--tenant-accent", tenant.accentColor);
  root.style.setProperty("--tenant-bg", tenant.bgColor);
  root.style.setProperty("--tenant-text", tenant.textColor);

  // Update favicon if tenant has one
  if (tenant.faviconUrl) {
    const link =
      (document.querySelector("link[rel='icon']") as HTMLLinkElement) ??
      document.createElement("link");
    link.rel = "icon";
    link.href = tenant.faviconUrl;
    if (!link.parentNode) document.head.appendChild(link);
  }

  // Update page title
  document.title = tenant.name;
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<TenantBranding | null>(null);
  const [siteName, setSiteName] = useState("tubeforge");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const apiBase = import.meta.env.VITE_API_URL ?? "";

    fetch(`${apiBase}/public/config`)
      .then((res) => res.json())
      .then((data) => {
        setSiteName(data.site?.name ?? "tubeforge");

        if (data.tenant) {
          const branding: TenantBranding = {
            slug: data.tenant.slug,
            name: data.tenant.name ?? data.site?.name ?? "tubeforge",
            tagline: data.site?.tagline ?? "",
            logoUrl: data.tenant.logoUrl ?? null,
            faviconUrl: data.tenant.faviconUrl ?? null,
            primaryColor: data.tenant.primaryColor ?? "#ff5b1f",
            accentColor: data.tenant.accentColor ?? "#06b6d4",
            bgColor: data.tenant.bgColor ?? "#ffffff",
            textColor: data.tenant.textColor ?? "#0a0a0a",
            videoPipeline: data.tenant.videoPipeline === "engine" ? "engine" : "legacy",
          };
          setTenant(branding);
          applyBrandingToCSS(branding);
        }
      })
      .catch((err) => {
        console.warn("Failed to load tenant config:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return (
    <TenantContext.Provider value={{ tenant, siteName, loading }}>
      {children}
    </TenantContext.Provider>
  );
}
